import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {Observable, map, } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from './user.model';
import { APIResponse } from 'src/app/shared/data-access/api-response.model';
import {RoleBulkImport} from "../feature/userManagement/importUsers/importUsers.component";
import {RoleTempInterface} from "../../dashboard/userProfile/userProfile.component";
import {catchApiError, catchErrorWith} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private httpClient = inject(HttpClient);

  fetchOrganizationUsers(): Observable<User[]> {
    const apiUrl = `${environment.baseUrl}/organization/users`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (!response.success) {
          return [];
        }
        return response.result?.items;
      })
    );
  }

  listAllUsers(page: number, limit: number, active_only?: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/users/search?search=&page=` + encodeParam(page) + '&limit=' + encodeParam(limit);

    if(active_only != undefined)
      apiUrl += `&active_only=${encodeParam(active_only)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  createUser(firstName: string, lastName: string, email: string, username: string, ruid: string, admit_code: string,
             school_code: string, role_id?: string, subject_id?: string, unit_id?: string, organization_id?: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/users/create`;

    let role = {
      role_id: role_id,
      subject_id: subject_id,
      unit_id: unit_id,
      organization_id: organization_id
    }

    let body = new HttpParams()
      .set('first_name', firstName)
      .set('last_name', lastName)
      .set('email', email)
      .set('username', username)
      .set('ruid', ruid)
      .set('admit_code', admit_code)
      .set('school_code', school_code)
      .set('role', JSON.stringify(role))

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  createBulkUsers(people: User[], roles: RoleBulkImport[]): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/organization/users/create/bulk`

    return this.httpClient.post<APIResponse>(apiUrl, `json=${JSON.stringify({
      people: people,
      roles: roles
    })}`).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  autoRosterSetup(rosterId: string, ip: string, port: string, fileLocation: string, protocol: string, time: string, role: RoleTempInterface, active: boolean, weeklyDay?: number, monthlyDate?: string, username?: string, key?: string, keySet?: boolean): Observable<boolean> {
    const apiUrl = `${environment.baseUrl}/rosterimport/setup`

    const hour = time.split(':')[0]
    const minute = time.split(':')[1]
    let month: number | null = null
    let day: number | null = null
    let orgID: number | null = null
    let subjectID: string | null = null
    let unitID: string | null = null
    let uriKey: string | null =  null
    if(role.subjects == null && role.units == null)
      orgID = 1
    if(role.subjects != null && role.subjects.length > 0)
      subjectID = role.subjects[0].subject_id
    if(role.units != null && role.units.length > 0)
      unitID = role.units[0].unit_id
    if(monthlyDate != undefined) {
      month = Number(monthlyDate.split('-')[1])
      day = Number(monthlyDate.split('-')[2])
    }
    if(keySet == undefined)
      keySet = false
    if(key != null)
      uriKey = encodeURIComponent(key)

    const requestData = {
      files: [{
        roles: [{
          rosterrole_id: 1,
          role_id: role.role_id,
          organization_id: orgID,
          subject_id: subjectID,
          unit_id: unitID
        }],
        rosterfile_id: 1,
        rostersource_id: rosterId,
        path: fileLocation,
      }],
      rostersource_id: rosterId,
      host: ip,
      port: port,
      protocol: protocol,
      username: username,
      key: uriKey,
      minute: minute,
      hour: hour,
      day: day,
      month: month,
      weekday: weeklyDay,
      active: active,
      keySet: keySet,
    }

    if(rosterId) {
      requestData.rostersource_id = rosterId;
    }

    return this.httpClient.post<APIResponse>(apiUrl, `json=${JSON.stringify(
      requestData
    )}`).pipe(
      map((response) => {
        return response.success
      }),
      catchErrorWith(false)
    )
  }

  getAutoRosterConfig(): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/rosterimport/view`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getAutoRosterConfigByRosterId(rosterId: string): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/rosterimport/view`
    return this.httpClient.get<APIResponse>(apiUrl, {params:{'rostersource_id': rosterId}}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getAutoRosterLogs(page: number, limit?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/rosterimport/logs?page=${encodeParam(page.toString())}`
    if(limit != null)
      apiUrl += `&limit=${encodeParam(limit.toString())}`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  searchUsers(display_name: string, page: number, limit: number) : Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/organization/users/search?search=${encodeParam(display_name)}&page=${encodeParam(page)}&limit=${encodeParam(limit)}`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  setActiveUser(user_id: number, active: boolean): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/organization/users/setactive?user_id=${encodeParam(user_id)}&active=${encodeParam(active)}`;

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  testRosterImportConnection(rostersource_id: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/rosterimport/test?rostersource_id=${encodeParam(rostersource_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  searchUserExamInstances(user_id: number, search: string, page: number, limit: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/organization/users/instances/search?user_id=${encodeParam(user_id)}&search=${encodeParam(search)}&page=${encodeParam(page)}&limit=${encodeParam(limit)}`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getSingleUserDetails(user_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/organization/users/details/findByID?user_id=${encodeParam(user_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }
}
